import { useEffect, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import Button from "@/components/Button";
import Dialog from "@/components/Dialog";
import EmailPreviewDialog from "@/components/EmailPreviewDialog";
import TextField from "@/components/TextField";
import { toFormat } from "@/lib/dateHelpers";
import { showErrorSnackbar, showSnackbar } from "@/lib/snackbarUtils";
import { useErrorBanner } from "@/providers/ErrorBannerProvider";
import { UPCOMING_MEMBER_ELECTION_DATES_QUERY } from "@/pages/people/membership-services/member-elections/upcoming-dates/queries";
import type { UpcomingMemberElectionDatesQueryResponse } from "@/pages/people/membership-services/member-elections/upcoming-dates/types";
import type { ApplicationType } from "../../types";
import type { RegistrationOrder } from "../types";
import {
  APPROVE_SUBMITTED_APPLICATION_MUTATION,
  UPDATE_SUBMITTED_APPLICATION_MUTATION,
  type ApproveSubmittedApplicationResponse,
  type UpdateSubmittedApplicationResponse
} from "../mutations";
import { MEMBERSHIP_APPLICATION_QUERY } from "../queries";
import { useMembershipApplicationApprovalEmailPreview } from "../hooks/useMembershipApplicationApprovalEmailPreview";

const CCMS_BAND_SYNC_WARNING_MARKER = "CCMS membership band sync failed";

interface ApproveApplicationDialogProps {
  open: boolean;
  onClose: () => void;
  applicationId: string;
  applicationType?: ApplicationType;
  onApproved?: (orders: { registrationOrder: RegistrationOrder; upcomingDuesOrder?: RegistrationOrder | null }) => void;
}

export default function ApproveApplicationDialog({
  open,
  onClose,
  applicationId,
  applicationType,
  onApproved
}: ApproveApplicationDialogProps) {
  const usesRegistrationOrderApprovalFlow = applicationType === "associate" || applicationType === "affiliate";
  const showElectionBullet = applicationType === "member";
  const { setError, clearError } = useErrorBanner();
  const [messageToApplicant, setMessageToApplicant] = useState("");
  const [internalNote, setInternalNote] = useState("");
  const [previewOpen, setPreviewOpen] = useState(false);
  const {
    loadPreview,
    resetPreview,
    preview,
    loading: previewLoading,
    error: previewError
  } = useMembershipApplicationApprovalEmailPreview(applicationId);

  useEffect(() => {
    if (open) {
      setMessageToApplicant("");
      setInternalNote("");
    } else {
      setPreviewOpen(false);
      resetPreview();
    }
  }, [open, resetPreview]);

  const { data: upcomingDatesData } = useQuery<UpcomingMemberElectionDatesQueryResponse>(
    UPCOMING_MEMBER_ELECTION_DATES_QUERY,
    { skip: !open || !showElectionBullet }
  );
  const nextElectionDate = upcomingDatesData?.upcomingMemberElectionDates?.[0]?.electionDate ?? null;
  const formattedNextElectionDate = nextElectionDate ? toFormat(nextElectionDate, "MMM dd, yyyy") : null;

  const mutationOptions = {
    refetchQueries: [{ query: MEMBERSHIP_APPLICATION_QUERY, variables: { id: applicationId } }],
    onCompleted: (data: ApproveSubmittedApplicationResponse | UpdateSubmittedApplicationResponse) => {
      const result = data.updateSubmittedApplication;
      if (!result.success) {
        setError(result.message);
        return;
      }

      clearError();

      const hasCcmsSyncWarning = result.message.includes(CCMS_BAND_SYNC_WARNING_MARKER);
      if (hasCcmsSyncWarning) {
        showErrorSnackbar(result.message);
      }

      const registrationOrder = usesRegistrationOrderApprovalFlow
        ? (data as ApproveSubmittedApplicationResponse).updateSubmittedApplication.application?.registrationOrder
        : null;
      const upcomingDuesOrder = usesRegistrationOrderApprovalFlow
        ? (data as ApproveSubmittedApplicationResponse).updateSubmittedApplication.application?.upcomingDuesOrder
        : null;

      if (usesRegistrationOrderApprovalFlow && registrationOrder && onApproved) {
        onApproved({ registrationOrder, upcomingDuesOrder });
        onClose();
        return;
      }

      if (!hasCcmsSyncWarning) {
        showSnackbar("Application approved");
      }
      onClose();
    },
    onError: (error: Error) => {
      setError(error.message);
    }
  };

  const [approveWithRegistrationOrder, { loading: approvingWithRegistrationOrder }] = useMutation(
    APPROVE_SUBMITTED_APPLICATION_MUTATION,
    mutationOptions
  );
  const [approveApplicationDefault, { loading: approvingDefault }] = useMutation(
    UPDATE_SUBMITTED_APPLICATION_MUTATION,
    mutationOptions
  );

  const approveApplication = usesRegistrationOrderApprovalFlow
    ? approveWithRegistrationOrder
    : approveApplicationDefault;
  const loading = usesRegistrationOrderApprovalFlow ? approvingWithRegistrationOrder : approvingDefault;

  const handlePreviewEmail = () => {
    setPreviewOpen(true);
    void loadPreview(messageToApplicant);
  };

  const handleApprove = () => {
    const trimmedMessage = messageToApplicant.trim();
    const trimmedNote = internalNote.trim();

    void approveApplication({
      variables: {
        input: {
          id: applicationId,
          status: "approved",
          ...(trimmedMessage ? { messageToApplicant: trimmedMessage } : {}),
          ...(trimmedNote ? { internalNote: trimmedNote } : {})
        }
      }
    });
  };

  return (
    <>
      <Dialog
        open={open}
        onClosed={onClose}
        headline="Approve Application"
        className="w-[600px] max-w-[95vw]"
        hasFormElements
        actions={
          <div className="flex w-full flex-wrap items-center justify-between gap-2">
            <Button
              variant="text"
              icon="mail"
              onClick={handlePreviewEmail}
              disabled={loading}
              testId="preview-email-button"
            >
              Preview Email
            </Button>
            <div className="flex flex-wrap justify-end gap-2">
              <Button variant="text" onClick={onClose} disabled={loading}>
                Cancel
              </Button>
              <Button
                variant="filled"
                icon="check"
                onClick={handleApprove}
                disabled={loading}
                testId="approve-application-submit"
              >
                {loading ? "Approving..." : "Approve"}
              </Button>
            </div>
          </div>
        }
      >
        <div
          className="max-h-[calc(85vh-12rem)] overflow-y-auto min-h-0"
          data-testid="approve-application-dialog-scroll"
        >
          <div className="flex flex-col gap-6 pr-1">
            <div className="text-body-medium text-sys-brand-on-surface-variant">
              <p>Are you sure you wish to approve this application?</p>
              <ul className="list-disc pl-5">
                <li>The applicant will be informed of the approval via an email and on their account</li>
                <li>
                  Any application details that have been updated by the applicant will be saved to their person profile,
                  and a new membership record will be created
                </li>
                <li>
                  Education credits will automatically be added to the applicant&rsquo;s profile if not already
                  accounted for
                </li>
                <li>The applicant will be issued an order for their new program registration fees</li>
                {showElectionBullet && (
                  <li>
                    {formattedNextElectionDate
                      ? `The applicant will automatically be elected to membership on ${formattedNextElectionDate}`
                      : "The applicant will automatically be elected to membership on the next available election date"}
                  </li>
                )}
              </ul>
            </div>
            <TextField
              label="Message to Applicant (Optional)"
              type="textarea"
              rows={3}
              value={messageToApplicant}
              onChangeText={setMessageToApplicant}
              disabled={loading}
              className="w-full"
            />
            <TextField
              label="Internal Note"
              type="textarea"
              rows={3}
              value={internalNote}
              onChangeText={setInternalNote}
              disabled={loading}
              className="w-full"
            />
          </div>
        </div>
      </Dialog>
      <EmailPreviewDialog
        open={previewOpen}
        onClosed={() => setPreviewOpen(false)}
        subject={previewLoading ? undefined : preview?.subject}
        html={previewLoading ? undefined : preview?.html}
        loading={previewLoading}
        errorMessage={previewLoading ? null : (previewError?.message ?? null)}
      />
    </>
  );
}
